import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Globe } from "lucide-react";

const SCENES = [
  {
    prompt: "a booking page for my barbershop",
    title: "Fade & Bloom",
    accent: "#0055FF",
    lines: ["Book a fresh cut", "Mon–Sat · 9am–7pm", "Pick a time →"],
  },
  {
    prompt: "a portfolio for a photographer",
    title: "Maya Studio",
    accent: "#111111",
    lines: ["Wedding & portrait", "Selected work", "Get in touch →"],
  },
  {
    prompt: "a waitlist for my new app",
    title: "Jotly",
    accent: "#7A3BFF",
    lines: ["Notes, reimagined", "Join 2,400 waiting", "Reserve your spot →"],
  },
];

export const PhoneMockup = () => {
  const [i, setI] = useState(0);
  const [typed, setTyped] = useState("");
  const [phase, setPhase] = useState("typing"); // typing -> building -> preview

  useEffect(() => {
    let t;
    const scene = SCENES[i];
    if (phase === "typing") {
      if (typed.length < scene.prompt.length) {
        t = setTimeout(() => setTyped(scene.prompt.slice(0, typed.length + 1)), 55);
      } else {
        t = setTimeout(() => setPhase("building"), 500);
      }
    } else if (phase === "building") {
      t = setTimeout(() => setPhase("preview"), 1200);
    } else if (phase === "preview") {
      t = setTimeout(() => {
        setPhase("typing");
        setTyped("");
        setI((v) => (v + 1) % SCENES.length);
      }, 2600);
    }
    return () => clearTimeout(t);
  }, [typed, phase, i]);

  const scene = SCENES[i];

  return (
    <div className="relative mx-auto w-[270px] sm:w-[300px]">
      {/* Bezel */}
      <div className="relative rounded-[42px] bg-ho-ink p-2.5 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.45)]">
        <div className="relative overflow-hidden rounded-[34px] bg-white h-[560px]">
          {/* notch */}
          <div className="absolute left-1/2 top-2 z-20 h-6 w-28 -translate-x-1/2 rounded-full bg-ho-ink" />
          {/* status bar */}
          <div className="flex items-center justify-between px-6 pt-3 text-[11px] font-semibold text-ho-ink/80">
            <span>9:41</span>
            <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> HandsOn</span>
          </div>

          {/* Preview area */}
          <div className="relative mt-2 h-[380px] mx-3 overflow-hidden rounded-2xl border border-black/5 bg-ho-bg">
            <AnimatePresence mode="wait">
              {phase === "preview" ? (
                <motion.div
                  key={`prev-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="h-full w-full"
                >
                  <div className="h-24 w-full" style={{ background: scene.accent }} />
                  <div className="-mt-8 mx-4 rounded-2xl bg-white p-4 shadow-sm">
                    <div className="font-display text-lg font-extrabold" style={{ color: scene.accent }}>
                      {scene.title}
                    </div>
                    <div className="mt-3 space-y-2">
                      {scene.lines.map((l, idx) => (
                        <motion.div
                          key={l}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.15 + idx * 0.12 }}
                          className="text-sm text-ho-ink/75"
                        >
                          {l}
                        </motion.div>
                      ))}
                    </div>
                    <div
                      className="mt-4 rounded-full py-2 text-center text-xs font-semibold text-white"
                      style={{ background: scene.accent }}
                    >
                      Live preview
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="build"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="grid h-full place-items-center"
                >
                  {phase === "building" ? (
                    <div className="flex flex-col items-center gap-3">
                      <div className="grid place-items-center w-12 h-12 rounded-2xl bg-ho-primary/10">
                        <Sparkles className="w-6 h-6 text-ho-primary animate-pulse" />
                      </div>
                      <p className="text-xs font-medium text-ho-muted">Building your site…</p>
                    </div>
                  ) : (
                    <p className="px-6 text-center text-xs text-ho-muted">Your preview appears here</p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Chat input */}
          <div className="absolute bottom-4 left-3 right-3">
            <div className="flex items-center gap-2 rounded-full border border-black/5 bg-white px-3 py-2.5 shadow-sm">
              <Sparkles className="w-4 h-4 text-ho-primary shrink-0" />
              <span className="text-[13px] text-ho-ink">
                {typed}
                {phase === "typing" && <span className="ml-0.5 inline-block h-4 w-[2px] animate-pulse bg-ho-primary align-middle" />}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
